import PropTypes from 'prop-types';
import { TABLE_ALIGN, TABLE_ALIGNS } from './constants';

// The column fields Table and DataTable both read. DataTable extends them with its data
// fields (accessor, cell, sortable…); neither may give one of these a second meaning.
export const TABLE_COLUMN_PROP_TYPES = {
    /** Stable id of the column */
    id: PropTypes.string.isRequired,
    /** Header content (already translated); may be a node */
    header: PropTypes.node.isRequired,
    /** Plain-text name, required when `header` is a node — used wherever the header must be text
     *  (accessible names, the stacked-layout label) */
    label: PropTypes.string,
    /** Horizontal alignment */
    align: PropTypes.oneOf(TABLE_ALIGNS),
    /** Number (px) or CSS length */
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Number (px) or CSS length */
    minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Number (px) or CSS length */
    maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Keep the header for screen readers only (an actions column) */
    srOnly: PropTypes.bool,
    /** Omit the column entirely */
    hidden: PropTypes.bool,
    /** Class on the column's body cells, where the table renders them */
    className: PropTypes.string,
    /** Class on the header cell */
    headerClassName: PropTypes.string,
};

/** A column definition, or a plain header string as shorthand for `{ id, header }` */
export const tableColumnPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.shape(TABLE_COLUMN_PROP_TYPES)]);

export const normalizeAlign = (align) => (TABLE_ALIGNS.includes(align) ? align : TABLE_ALIGN.LEFT);

// `header` may be a node (an icon, a tooltip), which cannot go in an attribute or an
// accessible name, so a column carrying one supplies `label`.
export const getColumnLabel = (column) => {
    if (typeof column.label === 'string') return column.label;
    if (typeof column.header === 'string') return column.header;
    if (typeof column.header === 'number') return String(column.header);
    return '';
};

// Drops empty entries (`{cond ? column : null}`) and hidden columns, expands the string
// shorthand and gives every column an id.
export const normalizeColumns = (columns) =>
    (Array.isArray(columns) ? columns : [])
        .filter((column) => column && !column.hidden)
        .map((column, index) => {
            if (typeof column === 'string') return { id: column, header: column };
            return { ...column, id: String(column.id ?? (getColumnLabel(column) || index)) };
        });

// `width` overrides the declared one (a resized column).
export const getColumnStyle = (column, width = column.width) => {
    const style = Object.fromEntries(
        [['width', width], ['minWidth', column.minWidth], ['maxWidth', column.maxWidth]].filter(
            ([, value]) => value != null && value !== '',
        ),
    );
    return Object.keys(style).length ? style : undefined;
};
